// ============================================================================
// AdminPage — ecran Administration gest (vague Admin, AD-A : shell + Audit)
// ============================================================================
// SHELL de l'ecran (P-ADM-8) : 7 onglets URL-reflected (?tab=overview|members|
// teams|remuneration|contrats|questions|audit — PAS reminders, STOP-11 : differe
// AD-G avec le mailer ACS, zero lien mort), rail AdminSideNav local, garde
// client 403. SEUL l'onglet AUDIT rend du contenu REEL en AD-A (useAdminAudit
// -> GET /api/admin/audit, lecture pure P-ADM-10) — il prouve le chemin
// bout-en-bout (garde serveur + repo + i18n + shell).
//
// AD-B : les onglets OVERVIEW (KpiCards du parc, bundle GET /overview — zero
// widget mail, P-ADM-7) et TEAMS (DataTable + creer/modifier via GsWizardDialog,
// bundle GET /teams) rendent leur contenu REEL. Chaque bundle n'est charge que
// quand son onglet est actif (P-ADM-8).
//
// AD-C : l'onglet MEMBRES rend son contenu REEL (MembersTab — fiches STOP-4a,
// statut d'emploi P-ADM-18, file d'inactivations P-ADM-17, recherche etat React
// P-ADM-14, AUCUN rekey STOP-5).
//
// AD-D : l'onglet REMUNERATION rend son contenu REEL (RemunerationTab —
// selection cible via liste AD-C + hub /api/management/:email/* EXISTANT,
// P-ADM-16 hydrate avant PUT, P-ADM-9 fractions/points). AUCUNE route
// /api/admin remuneration (FF-PII-05 chemin unique).
//
// AD-E : l'onglet CONTRATS rend son contenu REEL (ContractsTab — referentiel de
// modeles de contrat admin-wide : DataTable + creer/editer via ContractTemplateDialog,
// supprimer via ConfirmDialog destructif. C'est le CATALOGUE de gabarits, PAS
// l'emission d'un contrat a un employe ; M-163 : supprimer un gabarit n'affecte
// aucun contrat deja emis).
//
// AD-F : l'onglet QUESTIONS rend son contenu REEL (QuestionsTab — atelier de
// sondage : modele + surcouche + banque + apercu vivant, SANS le sous-onglet
// Courriel STOP-1). Les selecteurs sondage/equipe/periode sont de l'ETAT REACT
// leve ICI (P-ADM-14 : ils re-clent la query, jamais de reload). Le verrou
// question-coeur (P-ADM-4) est SERVEUR (assertCoreInvariant). DERNIER
// placeholder retire : les 7 onglets de la vague 1 sont vivants.
//
// Garde P-ADM-2 : la garde REELLE est le 403 SERVEUR (canAccessGestAdmin =
// admin SEUL ; director/manager/employee refuses). Le SPA ne DECIDE pas — il
// redirige le 403 vers /error/403, EN PLUS de la garde serveur (GA-05). La
// query audit est montee quel que soit l'onglet : elle sert de garde client au
// shell entier (bundle leger, TOP 100).

import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { PageHeader, EmptyState, SkeletonCard, QueryError, GS_COMPONENTS } from '@gs/ui';
import { KeyRound, ScrollText, Trash2, UserPlus } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useAdminAudit, type AdminAuditEvent } from '../../hooks/useAdminAudit';
import { useAdminOverview, useAdminTeams } from '../../hooks/useAdminTeams';
import { useAdminMembers } from '../../hooks/useAdminMembers';
import { useAdminContractTemplates } from '../../hooks/useAdminContractTemplates';
import { useAdminQuestions, type PulseSurveyType } from '../../hooks/useAdminQuestions';
import { useAdminReminders } from '../../hooks/useAdminReminders';
import { AdminSideNav } from '../../components/admin/AdminSideNav';
import { ADMIN_TAB_KEYS, type AdminTab } from '../../components/admin/AdminSideNav.constants';
import { SectionHeader } from '../../components/admin/SectionHeader';
import { OverviewTab } from '../../components/admin/OverviewTab';
import { TeamsTab } from '../../components/admin/TeamsTab';
import { MembersTab } from '../../components/admin/MembersTab';
import { RemunerationTab } from '../../components/admin/RemunerationTab';
import { ContractsTab } from '../../components/admin/ContractsTab';
import { QuestionsTab } from '../../components/admin/QuestionsTab';
import { RemindersTab } from '../../components/admin/RemindersTab';
import { GovernanceAccessTab } from '../../components/admin/GovernanceAccessTab';
import { PiiAccessLogTab } from '../../components/admin/PiiAccessLogTab';
import { formatDateTimeFr } from '../../lib/format/date';
import { useAdminExecutiveGrants } from '../../hooks/useAdminExecutiveGrants';

/** ?tab= hors liste -> defaut `overview`. */
function resolveTab(raw: string | null): AdminTab {
  return (ADMIN_TAB_KEYS as readonly string[]).includes(raw ?? '') ? (raw as AdminTab) : 'overview';
}

/** Cle i18n d'une action d'audit (les cles ne peuvent pas contenir de point). */
function actionLabelKey(action: string): string {
  return `gest:admin.audit.actions.${action.replace(/\./g, '_')}`;
}

export default function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tab = resolveTab(searchParams.get('tab'));

  // Le bundle audit (P-ADM-10) reste monte sur TOUS les onglets : il porte la
  // garde client du shell (403 -> /error/403). Les bundles AD-B ne sont charges
  // que quand leur onglet est actif (P-ADM-8).
  const q = useAdminAudit(!!user);
  const overviewQ = useAdminOverview(!!user && tab === 'overview');
  const teamsQ = useAdminTeams(!!user && tab === 'teams');
  // AD-D reutilise la liste membres AD-C pour la selection de cible
  // (pas de nouvelle route liste remuneration).
  const membersQ = useAdminMembers(!!user && (tab === 'members' || tab === 'remuneration' || tab === 'ce'));
  const contractsQ = useAdminContractTemplates(!!user && tab === 'contrats');
  // AD-G : bundle de l'onglet RAPPELS (contenus + kill-switch + equipes) charge
  // par onglet (P-ADM-8). Les mutations vivent DANS RemindersTab (etat React
  // P-ADM-14), invalidant ['gest','admin'].
  const remindersQ = useAdminReminders(!!user && tab === 'reminders');
  const executiveGrantsQ = useAdminExecutiveGrants(!!user && tab === 'ce');

  // AD-F (P-ADM-14) : selecteurs de l'atelier questions en ETAT REACT — ils
  // re-clent la query (jamais de reload). Le couple equipe/periode ne charge la
  // surcouche que COMPLET (coherence de scope, refusee cote serveur sinon).
  const [questionsSurvey, setQuestionsSurvey] = useState<PulseSurveyType>('member_pulse');
  const [questionsTeamId, setQuestionsTeamId] = useState('');
  const [questionsPeriod, setQuestionsPeriod] = useState('');
  const questionsQ = useAdminQuestions(!!user && tab === 'questions', {
    surveyType: questionsSurvey,
    teamId: questionsTeamId || null,
    period: questionsPeriod || null,
  });

  // Garde CLIENT P-ADM-2 : le 403 serveur (non-admin) -> ecran d'erreur standard.
  // La redirection ne REMPLACE pas la garde serveur, elle la double (GA-05).
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const activeQ =
    tab === 'overview'
      ? overviewQ
      : tab === 'teams'
        ? teamsQ
        : tab === 'members' || tab === 'remuneration'
          ? membersQ
          : tab === 'contrats'
            ? contractsQ
            : tab === 'questions'
              ? questionsQ
              : tab === 'reminders'
                ? remindersQ
                : tab === 'ce'
                  ? executiveGrantsQ
                  : q;

  const refreshLoading =
    tab === 'ce'
      ? executiveGrantsQ.isFetching || membersQ.isFetching
      : activeQ.isFetching;

  function handleRefresh() {
    if (tab === 'ce') {
      void executiveGrantsQ.refetch();
      void membersQ.refetch();
      return;
    }
    void activeQ.refetch();
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:admin.title')}
        description={t('gest:admin.description')}
        onRefresh={handleRefresh}
        refreshLoading={refreshLoading}
      />
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <AdminSideNav active={tab} />
        <div className="min-w-0 flex-1">
          {tab === 'audit' ? (
            <AuditTab
              isLoading={q.isLoading}
              isError={q.isError}
              events={q.data?.events}
              canViewPiiAccessLog={q.data?.viewer.canViewPiiAccessLog === true}
              onRetry={() => void q.refetch()}
            />
          ) : tab === 'overview' ? (
            <OverviewTab
              isLoading={overviewQ.isLoading}
              isError={overviewQ.isError}
              data={overviewQ.data}
              onRetry={() => void overviewQ.refetch()}
            />
          ) : tab === 'teams' ? (
            <TeamsTab
              isLoading={teamsQ.isLoading}
              isError={teamsQ.isError}
              data={teamsQ.data}
              onRetry={() => void teamsQ.refetch()}
            />
          ) : tab === 'members' ? (
            <MembersTab
              isLoading={membersQ.isLoading}
              isError={membersQ.isError}
              data={membersQ.data}
              onRetry={() => void membersQ.refetch()}
            />
          ) : tab === 'remuneration' ? (
            <RemunerationTab
              isLoading={membersQ.isLoading}
              isError={membersQ.isError}
              data={membersQ.data}
              onRetry={() => void membersQ.refetch()}
            />
          ) : tab === 'contrats' ? (
            <ContractsTab
              isLoading={contractsQ.isLoading}
              isError={contractsQ.isError}
              data={contractsQ.data}
              onRetry={() => void contractsQ.refetch()}
            />
          ) : tab === 'questions' ? (
            <QuestionsTab
              isLoading={questionsQ.isLoading}
              isError={questionsQ.isError}
              data={questionsQ.data}
              onRetry={() => void questionsQ.refetch()}
              surveyType={questionsSurvey}
              onSurveyTypeChange={setQuestionsSurvey}
              teamId={questionsTeamId}
              onTeamIdChange={setQuestionsTeamId}
              period={questionsPeriod}
              onPeriodChange={setQuestionsPeriod}
            />
          ) : tab === 'reminders' ? (
            <RemindersTab
              isLoading={remindersQ.isLoading}
              isError={remindersQ.isError}
              data={remindersQ.data}
              onRetry={() => void remindersQ.refetch()}
            />
          ) : tab === 'ce' ? (
            <GovernanceAccessTab
              grantsLoading={executiveGrantsQ.isLoading}
              grantsError={executiveGrantsQ.isError}
              grantsData={executiveGrantsQ.data}
              membersLoading={membersQ.isLoading}
              membersError={membersQ.isError}
              membersData={membersQ.data}
              onRetryGrants={() => void executiveGrantsQ.refetch()}
              onRetryMembers={() => void membersQ.refetch()}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ═══ Onglet AUDIT (P-ADM-10) — le seul contenu REEL d'AD-A ════════════════════
// Porte du PoC components/admin/audit-section.tsx : liste des 100 derniers
// evenements (action libellee + cible + phrase de metadata + acteur + date).
// Les ~60 libelles d'action vivent dans le catalogue i18n (FR+EN) sous
// admin.audit.actions.* ; une action inconnue (vocabulaire futur) retombe sur
// l'action brute (defaultValue) — le journal n'est jamais bloque par l'union.

function actionIcon(action: string) {
  if (action.endsWith('.created')) return <UserPlus size={16} aria-hidden="true" />;
  if (action.endsWith('.deleted') || action.endsWith('.removed')) {
    return <Trash2 size={16} aria-hidden="true" />;
  }
  return <ScrollText size={16} aria-hidden="true" />;
}

// Sous-onglets du Journal : metier (gest.AuditEvents) + acces PII (gestionnaire).

type AuditSubTab = 'business' | 'pii';

function AuditTab({
  isLoading,
  isError,
  events,
  canViewPiiAccessLog,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  events: AdminAuditEvent[] | undefined;
  canViewPiiAccessLog: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState<AuditSubTab>('business');

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !events) {
    return <QueryError message={t('gest:admin.audit.loadError')} onRetry={onRetry} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        className="flex flex-wrap gap-1 rounded-xl border border-slate-200 p-1 dark:border-slate-700"
        role="tablist"
        aria-label={t('gest:admin.audit.subTabs.label')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'business'}
          className={`touch-manipulation rounded-lg px-3 py-2 text-field transition-colors ${
            subTab === 'business'
              ? 'bg-brand-50 font-medium text-brand-600 dark:bg-brand-950 dark:text-brand-400'
              : 'text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'
          }`}
          onClick={() => setSubTab('business')}
        >
          {t('gest:admin.audit.subTabs.business')}
        </button>
        {canViewPiiAccessLog ? (
          <button
            type="button"
            role="tab"
            aria-selected={subTab === 'pii'}
            className={`touch-manipulation rounded-lg px-3 py-2 text-field transition-colors ${
              subTab === 'pii'
                ? 'bg-brand-50 font-medium text-brand-600 dark:bg-brand-950 dark:text-brand-400'
                : 'text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'
            }`}
            onClick={() => setSubTab('pii')}
          >
            {t('gest:admin.audit.subTabs.piiAccess')}
          </button>
        ) : null}
      </div>

      {subTab === 'pii' && canViewPiiAccessLog ? (
        <PiiAccessLogTab enabled />
      ) : (
        <BusinessAuditPanel events={events} />
      )}
    </div>
  );
}

function BusinessAuditPanel({ events }: { events: AdminAuditEvent[] }) {
  const { t } = useTranslation();

  // Metadonnees -> phrase lisible (champs surs seulement).
  const describeMeta = (event: AdminAuditEvent): string | null => {
    const m = event.metadata ?? {};
    if (event.action === 'user.manager_changed') {
      return `${m.from ?? t('gest:admin.audit.metaDefault')} -> ${m.to ?? t('gest:admin.audit.metaDefault')}`;
    }
    if (event.action === 'user.created' && m.role) {
      return t('gest:admin.audit.metaInitialRole', { role: m.role });
    }
    if (m.from && m.to) return `${m.from} -> ${m.to}`;
    if (typeof m.title === 'string' && m.title) return `« ${m.title} »`;
    return null;
  };

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-5 p-5`}>
      <SectionHeader
        title={t('gest:admin.audit.title')}
        description={t('gest:admin.audit.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.audit.count', { n: events.length })}
        </span>
      </SectionHeader>

      {events.length === 0 ? (
        <EmptyState
          icon={<ScrollText size={48} aria-hidden="true" />}
          title={t('gest:admin.audit.empty')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {events.map((event) => {
            const meta = describeMeta(event);
            return (
              <li
                key={event.id}
                className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700"
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {actionIcon(event.action)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-field font-medium text-slate-800 dark:text-slate-100">
                      {t(actionLabelKey(event.action), { defaultValue: event.action })}
                    </span>
                    <span className="text-chip text-slate-500 dark:text-slate-400">
                      {t('gest:admin.audit.on', { target: event.targetId })}
                    </span>
                    {meta ? (
                      <span className="text-chip text-slate-500 dark:text-slate-400">
                        {'·'} {meta}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-chip text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <KeyRound size={12} aria-hidden="true" />
                      {event.actorEmail}
                    </span>
                    <time dateTime={event.createdAt}>{formatDateTimeFr(event.createdAt)}</time>
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

